import { DirectoryLab } from './directory-browser-config';
import { clusterWorldPoints } from './supplier-map-clusters';

/** What the map needs to remove the pins again. */
export type SupplierMapPinLayer = { setMap: (map: google.maps.Map | null) => void };

/** One pin: the lab, where it sits, and what a click on it does. */
export type SupplierMapPinSpec = {
    lab: DirectoryLab;
    position: google.maps.LatLngLiteral;
    onClick: () => void;
};

const LOGO_MAX_WIDTH = 80;
const LOGO_MAX_HEIGHT = 32;

const initialsOf = (name?: string | null) =>
    (name ?? '')
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((word) => word[0]?.toUpperCase())
        .join('');

const PIN_CLASS = 'dh-map-pin';
const CLUSTER_CLASS = 'dh-map-cluster';
const POSITION_CLASS = 'dh-map-position';
const STYLE_ELEMENT_ID = 'dh-map-pin-styles';
/** A cluster whose labs share one spot zooms in this much instead of fitting its bounds. */
const CLUSTER_ZOOM_STEP = 2;
/**
 * DD-3698: a bubble whose labs all sit within this many degrees (~30 m) is one address —
 * zooming in never splits it, so a click lists them instead. Past STREET_ZOOM a bubble lists
 * its labs too: they are a building apart at most.
 */
const SAME_PLACE_DEGREES = 0.0003;
const STREET_ZOOM = 18;
const CLUSTER_FIT_PADDING_PX = 64;
/** Items this far outside the visible map stay in the DOM, so a short pan shows no gap. */
const VIEW_MARGIN_PX = 200;

/**
 * DD-3696: the pins' look lives in one stylesheet, and the pointer is a pseudo-element, so
 * a pin is two elements with only left/top inline. Google repositions every pin on every
 * zoom frame, and each inline write restyles the pin and its children: with a dozen inline
 * properties and two pointer spans per pin, that restyling made zooming slow.
 */
const PIN_STYLES = `
.${PIN_CLASS} {
    position: absolute;
    transform: translate(-50%, calc(-100% - 7px));
    display: flex;
    align-items: center;
    justify-content: center;
    min-width: 44px;
    padding: 6px 10px;
    background: #fff;
    border: 1px solid rgba(0, 0, 0, 0.2);
    border-radius: 6px;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.3);
    cursor: pointer;
}
.${PIN_CLASS}::after {
    content: '';
    position: absolute;
    left: 50%;
    bottom: -7px;
    margin-left: -6px;
    border-left: 6px solid transparent;
    border-right: 6px solid transparent;
    border-top: 7px solid #fff;
}
.${PIN_CLASS} > img {
    display: block;
    max-width: ${LOGO_MAX_WIDTH}px;
    max-height: ${LOGO_MAX_HEIGHT}px;
    object-fit: contain;
}
.${CLUSTER_CLASS} {
    position: absolute;
    transform: translate(-50%, -50%);
    min-width: 36px;
    height: 36px;
    padding: 0 8px;
    border-radius: 18px;
    border: 3px solid rgba(255, 255, 255, 0.9);
    background: #1f5fa6;
    color: #fff;
    font-size: 14px;
    font-weight: 600;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.3);
    cursor: pointer;
}
.${POSITION_CLASS} {
    position: absolute;
    transform: translate(-50%, -50%);
    width: 16px;
    height: 16px;
    border-radius: 50%;
    border: 3px solid #fff;
    background: #1a73e8;
    box-shadow: 0 0 0 6px rgba(26, 115, 232, 0.25);
    pointer-events: none;
}
.${PIN_CLASS} > span {
    font-size: 13px;
    font-weight: 600;
    line-height: ${LOGO_MAX_HEIGHT}px;
    color: #444;
}
`;

const ensurePinStyles = () => {
    if (document.getElementById(STYLE_ELEMENT_ID)) return;
    const style = document.createElement('style');
    style.id = STYLE_ELEMENT_ID;
    style.textContent = PIN_STYLES;
    document.head.appendChild(style);
};

const initialsElement = (name?: string | null) => {
    const initials = document.createElement('span');
    initials.textContent = initialsOf(name);
    return initials;
};

/** A white tag with the lab's logo (or initials) and a pointer at its bottom centre. */
const pinElement = (lab: DirectoryLab, index: number) => {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = PIN_CLASS;
    button.title = lab.name ?? '';
    button.setAttribute('aria-label', lab.name ?? '');
    button.dataset.test = `supplier-map.pin-${lab._id}`;
    button.dataset.pinIndex = String(index);
    if (lab.logoSignedUrl) {
        const logo = document.createElement('img');
        logo.src = lab.logoSignedUrl;
        logo.alt = '';
        logo.decoding = 'async';
        logo.onerror = () => logo.replaceWith(initialsElement(lab.name));
        button.appendChild(logo);
    } else {
        button.appendChild(initialsElement(lab.name));
    }
    return button;
};

const clusterElement = () => {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = CLUSTER_CLASS;
    button.dataset.test = 'supplier-map.cluster';
    return button;
};

/** Points a pooled bubble at its cluster, touching the DOM only where something changed. */
const showCluster = (button: HTMLElement, count: number, index: number, label: string) => {
    if (button.textContent !== String(count)) button.textContent = String(count);
    if (button.title !== label) {
        button.title = label;
        button.setAttribute('aria-label', label);
    }
    button.dataset.clusterIndex = String(index);
};

/** Whether the positions are one address (all within SAME_PLACE_DEGREES of each other). */
const isOnePlace = (positions: google.maps.LatLng[]) => {
    const latitudes = positions.map((position) => position.lat());
    const longitudes = positions.map((position) => position.lng());
    return (
        Math.max(...latitudes) - Math.min(...latitudes) <= SAME_PLACE_DEGREES &&
        Math.max(...longitudes) - Math.min(...longitudes) <= SAME_PLACE_DEGREES
    );
};

/** One thing on the map: a lab's own pin, or a bubble standing for several labs. */
type MapItem = {
    element: HTMLElement;
    position: google.maps.LatLng;
    members: number[];
    /** All members at one address: a click lists them, as zooming never splits them. */
    isSamePlace?: boolean;
};

/**
 * DD-3674: map pins showing each lab's logo, small. OverlayView rather than Marker icons,
 * so a wide logo keeps its proportions (and no Map ID is needed).
 * DD-3696: one overlay for every pin, with grid clustering. Labs that would overlap at the
 * current zoom become one bubble with their count; clicking it zooms in on them. Clusters
 * are rebuilt only when the zoom level changes, and draw() just places the current items,
 * so far fewer elements move on each zoom frame.
 */
export const createSupplierMapPinLayer = (
    maps: typeof google.maps,
    pins: SupplierMapPinSpec[],
    clusterLabel: (count: number, isSamePlace: boolean) => string,
    /** DD-3698: a bubble that zooming cannot split — the labs it stands for. */
    onShowList: (labs: DirectoryLab[]) => void,
): SupplierMapPinLayer => {
    ensurePinStyles();
    const positions = pins.map((pin) => new maps.LatLng(pin.position));
    const pinElements = pins.map((pin, index) => pinElement(pin.lab, index));

    class LogoPinLayer extends maps.OverlayView {
        private readonly container = document.createElement('div');
        private items: MapItem[] = [];
        private clusteredZoom?: number;
        /** The items now in the DOM: only those on or near the visible map. */
        private readonly attached = new Set<HTMLElement>();
        /** Bubble elements, reused across zoom levels rather than created anew. */
        private readonly clusterPool: HTMLElement[] = [];

        constructor() {
            super();
            this.container.addEventListener('click', (event) => this.handleClick(event));
        }

        private handleClick(event: MouseEvent) {
            const target = (event.target as HTMLElement).closest<HTMLElement>('[data-pin-index], [data-cluster-index]');
            if (!target) return;
            event.stopPropagation();
            if (target.dataset.pinIndex !== undefined) {
                pins[Number(target.dataset.pinIndex)]?.onClick();
                return;
            }
            const cluster = this.items[Number(target.dataset.clusterIndex)];
            if (!cluster) return;
            const zoom = (this.getMap() as google.maps.Map | null)?.getZoom() ?? 0;
            if (cluster.isSamePlace || zoom >= STREET_ZOOM) {
                onShowList(cluster.members.map((member) => pins[member].lab));
                return;
            }
            this.zoomInto(cluster);
        }

        private zoomInto(cluster: MapItem) {
            const map = this.getMap() as google.maps.Map | null;
            if (!map) return;
            const bounds = new maps.LatLngBounds();
            cluster.members.forEach((member) => bounds.extend(positions[member]));
            const spansArea = !bounds.getNorthEast().equals(bounds.getSouthWest());
            if (spansArea) {
                map.fitBounds(bounds, CLUSTER_FIT_PADDING_PX);
            } else {
                map.setCenter(cluster.position);
                map.setZoom((map.getZoom() ?? 0) + CLUSTER_ZOOM_STEP);
            }
        }

        /** Groups the labs for this zoom level and puts their pins and bubbles in the DOM. */
        private cluster(map: google.maps.Map, zoom: number) {
            const projection = map.getProjection();
            if (!projection) return;
            const scale = 2 ** zoom;
            const world = positions.map((position) => {
                const point = projection.fromLatLngToPoint(position);
                return { x: (point?.x ?? 0) * scale, y: (point?.y ?? 0) * scale };
            });
            let pooled = 0;
            this.items = clusterWorldPoints(world).map((members, index) => {
                if (members.length === 1) {
                    return { element: pinElements[members[0]], position: positions[members[0]], members };
                }
                const latitude = members.reduce((sum, member) => sum + positions[member].lat(), 0) / members.length;
                const longitude = members.reduce((sum, member) => sum + positions[member].lng(), 0) / members.length;
                const element = this.clusterPool[pooled] ?? clusterElement();
                this.clusterPool[pooled] = element;
                pooled += 1;
                const isSamePlace = isOnePlace(members.map((member) => positions[member]));
                showCluster(element, members.length, index, clusterLabel(members.length, isSamePlace));
                return { element, position: new maps.LatLng(latitude, longitude), members, isSamePlace };
            });
            // Elements that are still items stay in the DOM; draw() moves them and attaches the
            // new ones in view. Restyling everything at once on every level was a visible hitch.
            const current = new Set(this.items.map((item) => item.element));
            this.attached.forEach((element) => {
                if (current.has(element)) return;
                element.remove();
                this.attached.delete(element);
            });
            this.clusteredZoom = zoom;
        }

        onAdd() {
            this.getPanes()?.overlayMouseTarget.appendChild(this.container);
        }

        draw() {
            const map = this.getMap() as google.maps.Map | null;
            const projection = this.getProjection();
            if (!map || !projection) return;
            const zoom = Math.round(map.getZoom() ?? 0);
            if (zoom !== this.clusteredZoom) this.cluster(map, zoom);
            const isInView = this.viewTest(map, projection);
            // Every position first, then every DOM change and write, so the browser lays out once.
            const points = this.items.map((item) => projection.fromLatLngToDivPixel(item.position));
            points.forEach((point, index) => {
                const { element } = this.items[index];
                if (!point || !isInView(point)) {
                    if (this.attached.delete(element)) element.remove();
                    return;
                }
                if (!this.attached.has(element)) {
                    this.container.appendChild(element);
                    this.attached.add(element);
                }
                element.style.left = `${point.x}px`;
                element.style.top = `${point.y}px`;
            });
        }

        /**
         * DD-3698: whether a point is on or near the visible map. Thousands of clinics zoomed
         * to street level are mostly single pins, and every pin in the DOM is restyled on each
         * zoom frame (seconds per frame with ~1,300), so off-screen items leave the DOM.
         */
        private viewTest(map: google.maps.Map, projection: google.maps.MapCanvasProjection) {
            const bounds = map.getBounds();
            const northEast = bounds && projection.fromLatLngToDivPixel(bounds.getNorthEast());
            const southWest = bounds && projection.fromLatLngToDivPixel(bounds.getSouthWest());
            if (!northEast || !southWest) return () => true;
            const left = Math.min(southWest.x, northEast.x) - VIEW_MARGIN_PX;
            const right = Math.max(southWest.x, northEast.x) + VIEW_MARGIN_PX;
            const top = Math.min(southWest.y, northEast.y) - VIEW_MARGIN_PX;
            const bottom = Math.max(southWest.y, northEast.y) + VIEW_MARGIN_PX;
            return (point: google.maps.Point) =>
                point.x >= left && point.x <= right && point.y >= top && point.y <= bottom;
        }

        onRemove() {
            this.container.remove();
        }
    }
    return new LogoPinLayer();
};

/** DD-3696: the browser's position as a blue dot, like Google Maps' own. */
export const createPositionDot = (maps: typeof google.maps, position: google.maps.LatLngLiteral) => {
    ensurePinStyles();
    class PositionDot extends maps.OverlayView {
        private readonly element = document.createElement('div');

        constructor() {
            super();
            this.element.className = POSITION_CLASS;
            this.element.dataset.test = 'supplier-map.my-position';
        }

        onAdd() {
            this.getPanes()?.overlayLayer.appendChild(this.element);
        }

        draw() {
            const point = this.getProjection()?.fromLatLngToDivPixel(new maps.LatLng(position));
            if (!point) return;
            this.element.style.left = `${point.x}px`;
            this.element.style.top = `${point.y}px`;
        }

        onRemove() {
            this.element.remove();
        }
    }
    return new PositionDot();
};
